import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import {
  MarketplacePropertyCategory_ListingMediaState as ListingMediaState,
  MarketplacePropertyCategory_ListingVideoPlaybackEvent_Milestone as PlaybackMilestone,
} from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import * as Sentry from '@sentry/nextjs';
import { IconPlayerPlayFilled, IconX } from '@tabler/icons-react';
import { useInView } from 'react-intersection-observer';

import { GalleryMediaActions } from './GalleryMediaActions';
import { listingReelTransformUrl } from './listingReelTransform';
import type { PdpMediaSlot } from './usePdpMediaSlot';

const QUARTILES = [
  { fraction: 0.25, milestone: PlaybackMilestone.QUARTILE_25 },
  { fraction: 0.5, milestone: PlaybackMilestone.QUARTILE_50 },
  { fraction: 0.75, milestone: PlaybackMilestone.QUARTILE_75 },
] as const;

/**
 * `fill` makes the whole hero the play target; other triggers add a discrete
 * button and leave the surface's own click alone.
 */
type HeroVideoTrigger = 'fill' | 'control' | 'gallery-pill';

/**
 * Transform width per breakpoint twin: `control` renders in the mobile
 * carousel, `fill` on the desktop gallery. The twin, not a measurement, picks
 * the rung — both stay mounted from first render, so a viewport read would be
 * wrong under SSR and swapping `src` later is off the table.
 */
const REEL_WIDTH_BY_TRIGGER: Record<HeroVideoTrigger, number> = {
  fill: 854,
  control: 480,
  'gallery-pill': 854,
};
const PLAY_CONTROL_CLASSES: Record<HeroVideoTrigger, string> = {
  fill: 'absolute inset-0 size-full rounded-none bg-transparent',
  control: 'absolute bottom-5 left-6 size-16 rounded-2xl shadow-md motion-safe:active:scale-95',
  'gallery-pill': 'gallery-action-button gallery-media-action',
};

/** A `timeupdate` gap wider than this is a seek, not playback. */
const SEEK_GAP_SECONDS = 1.5;

const PLAYBACK_ERROR_MESSAGE = 'We couldn’t play this video. Please try again.';

/** Omits either value the element cannot report yet, which must stay apart from a real zero. */
const playbackPosition = (video: HTMLVideoElement) => {
  const { currentTime, duration } = video;
  return {
    ...(Number.isFinite(currentTime) && { positionSeconds: Math.round(currentTime) }),
    ...(Number.isFinite(duration) && duration > 0 && { durationSeconds: Math.round(duration) }),
  };
};

type HeroWalkthroughVideoProps = {
  videoUrl: string;
  /** Join key every playback readout uses. */
  addressToken: string;
  addressLabel: string | null;
  slot: PdpMediaSlot;
  /**
   * `fill` makes the whole hero the play target, replacing the tile's own
   * click. `control` leaves the surface alone and offers a discrete button,
   * which the mobile carousel needs because one tap target serves every slide.
   */
  trigger: HeroVideoTrigger;
  /** Overrides the trigger's transform width when the surface is wider (e.g. the desktop map drawer). */
  reelWidth?: number;
  galleryTour3dUrl?: string;
};

/**
 * Walkthrough video layered over a listing's hero photo, dormant until play.
 *
 * Hidden with opacity, not `hidden`: a display:none video cannot be played in
 * every browser. The play button's white ground and dark glyph are literal
 * rather than tokens because both sit on photography.
 */
export const HeroWalkthroughVideo = ({
  videoUrl,
  addressToken,
  addressLabel,
  slot,
  trigger,
  reelWidth,
  galleryTour3dUrl,
}: HeroWalkthroughVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Whatever unmounts the control the viewer was on has to say where focus
  // goes, or it falls to the document and the Escape handler -- which only sees
  // keys bubbling from a focused descendant -- becomes unreachable. Claimed by
  // the press and by a failure, and placed when the replacement mounts.
  const pendingFocus = useRef<'play' | 'close' | null>(null);
  // `preventScroll` because focus otherwise scrolls its target into view, which
  // would drag a viewer reading the listing details back up to the hero.
  const playFocusRef = useCallback((node: HTMLElement | null) => {
    if (node && pendingFocus.current === 'play') {
      pendingFocus.current = null;
      node.focus({ preventScroll: true });
    }
  }, []);
  const closeFocusRef = useCallback((node: HTMLElement | null) => {
    if (node && pendingFocus.current === 'close') {
      pendingFocus.current = null;
      node.focus({ preventScroll: true });
    }
  }, []);
  const [isActive, setIsActive] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  // One report per mount, deliberately: a failed load fires both `error` and a
  // `play()` rejection, and this is the denominator for the rate of exposures
  // recorded against a hero that showed photos. It makes repeat failures of the
  // same asset after a retry invisible, which that rate does not need.
  const hasReportedFailure = useRef(false);
  // Quartiles credit seconds actually watched, not the position reached: a
  // watermark on position would be promoted by the first ordinary tick after a
  // seek, so a scrub would still report a full funnel for a viewer who watched
  // nothing.
  const watchedSeconds = useRef(0);
  const lastTick = useRef(0);
  const resetWatchProgress = useCallback(() => {
    watchedSeconds.current = 0;
    lastTick.current = 0;
  }, []);

  // Both breakpoints' heroes stay mounted and are hidden with CSS, so a
  // viewport crossing between them would otherwise leave a video playing in a
  // display:none subtree, reporting milestones nobody can see. Pausing is the
  // whole job: collapsing back to the play button would also punish a viewer
  // who merely scrolled down to the listing details and back.
  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (!inView) {
        videoRef.current?.pause();
      }
    },
  });

  const reportFailure = useCallback(
    (cause: string) => {
      setHasFailed(true);
      pendingFocus.current = 'play';
      setIsActive(false);
      if (hasReportedFailure.current) {
        return;
      }
      hasReportedFailure.current = true;
      // A media error can carry the whole request; report only bounded metadata.
      Sentry.captureException(new Error('PDP walkthrough video playback failed'), {
        tags: { feature: 'marketplace-property' },
        extra: { addressToken, cause, mediaErrorCode: videoRef.current?.error?.code },
      });
    },
    [addressToken],
  );

  const handlePlayClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    setHasFailed(false);
    resetWatchProgress();
    pendingFocus.current = 'close';
    slot.selectMediaState(ListingMediaState.VIDEO);
    // Explicit rather than relying on the state change: `selectMediaState`
    // resets the session only when the slot actually moves, so a retry, or a
    // press on the breakpoint twin, would be deduped against a session this
    // playback never took part in and report no play start at all.
    slot.startPlaybackSession();
    setIsActive(true);
    // Inside the click handler: the permission to play belongs to the gesture.
    video.play().catch((error: unknown) => {
      // Pausing a pending `play()` rejects it, so a dismiss mid-load arrives
      // here as an abort. Reporting it would blame the viewer's own action.
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      reportFailure('play-rejected');
    });
  }, [reportFailure, resetWatchProgress, slot]);

  const handleDismiss = useCallback(() => {
    const video = videoRef.current;
    video?.pause();
    // Rewound because leaving the slot ends the playback session, so the next
    // press counts its milestones afresh. Resuming at the old position would
    // re-report every quartile already passed inside one `timeupdate`.
    if (video) {
      video.currentTime = 0;
    }
    pendingFocus.current = 'play';
    resetWatchProgress();
    setIsActive(false);
    slot.selectMediaState(ListingMediaState.PHOTOS);
  }, [resetWatchProgress, slot]);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      slot.trackPlaybackMilestone(PlaybackMilestone.PLAY_START, playbackPosition(video));
    }
  }, [slot]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const { currentTime, duration } = video;
    if (!Number.isFinite(duration) || duration <= 0) {
      return;
    }
    const advanced = currentTime - lastTick.current;
    lastTick.current = currentTime;
    if (advanced > 0 && advanced <= SEEK_GAP_SECONDS) {
      watchedSeconds.current += advanced;
    }

    const position = playbackPosition(video);
    for (const { fraction, milestone } of QUARTILES) {
      if (watchedSeconds.current >= duration * fraction) {
        slot.trackPlaybackMilestone(milestone, position);
      }
    }
  }, [slot]);

  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      slot.trackPlaybackMilestone(PlaybackMilestone.COMPLETE, playbackPosition(video));
    }
    // A native replay opens a new session, which must not inherit this one's
    // progress and report every quartile on its first tick.
    resetWatchProgress();
  }, [resetWatchProgress, slot]);

  const handleError = useCallback(() => reportFailure('media-error'), [reportFailure]);

  // The mobile hero mounts inside `Carousel`, which drives slides from
  // `onPointerDown` and `onKeyDown` on its root. Without this a drag on the
  // seek bar also drags the photo track, and an arrow key seeks and changes
  // slide at once.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isActive) {
        return;
      }
      event.stopPropagation();
      if (event.key === 'Escape') {
        handleDismiss();
      }
    },
    [handleDismiss, isActive],
  );

  // The mini PDP closes on a document-level capture-phase Escape listener,
  // which fires before the onKeyDown above on the wrapper. Register this one
  // in capture too, at the video container, so dismissing playback wins over
  // tearing down the drawer the video plays in.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const dismissOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleDismiss();
      }
    };
    document.addEventListener('keydown', dismissOnEscape, true);
    return () => document.removeEventListener('keydown', dismissOnEscape, true);
  }, [handleDismiss, isActive]);

  const stopPointerPropagation = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  }, []);

  // Gating on asset availability instead would put the treatment media in
  // reach of the arm it is measured against.
  if (slot.assignedState !== ListingMediaState.VIDEO) {
    return null;
  }

  const ofAddress = addressLabel ? ` of ${addressLabel}` : '';
  const isGalleryPill = trigger === 'gallery-pill';
  const playControl = !isActive && (
    <Box
      ref={playFocusRef}
      aria-label={isGalleryPill ? `Play video${ofAddress}` : `Play walkthrough video${ofAddress}`}
      analyticsName="marketplace-pdp-walkthrough-video-play"
      onClick={handlePlayClick}
      className={cn(
        'pointer-events-auto z-[2] cursor-pointer items-center justify-center border-0 bg-white p-0 text-black',
        PLAY_CONTROL_CLASSES[trigger],
      )}
    >
      {trigger === 'fill' && (
        <Box className="size-16 items-center justify-center rounded-full bg-white text-black shadow-md">
          <IconPlayerPlayFilled className="size-7" />
        </Box>
      )}
      {isGalleryPill && (
        <>
          <IconPlayerPlayFilled size={18} aria-hidden="true" />
          <span className="gallery-media-label">Play</span>
        </>
      )}
      {trigger === 'control' && <IconPlayerPlayFilled className="size-8" />}
    </Box>
  );

  return (
    <Box
      ref={inViewRef}
      className="pointer-events-none absolute inset-0 items-center justify-center"
      onKeyDown={handleKeyDown}
      onPointerDown={stopPointerPropagation}
    >
      <Box
        role="alert"
        className={cn(
          // Above the play control, which shares this corner: a failure brings
          // the control back on the same tick that raises the message, so the
          // opaque button would otherwise paint over the only feedback given.
          'pointer-events-none absolute bottom-3 left-3 right-3 z-[3] rounded-lg px-3 py-2',
          hasFailed && 'bg-od-bg-primary',
        )}
      >
        {hasFailed && (
          <Text as="span" className="text-body-sm text-od-content-error">
            {PLAYBACK_ERROR_MESSAGE}
          </Text>
        )}
      </Box>
      {isGalleryPill
        ? !isActive && (
            <GalleryMediaActions
              videoAction={playControl}
              tour3dUrl={galleryTour3dUrl}
              addressLabel={addressLabel}
            />
          )
        : playControl}
      <video
        ref={videoRef}
        src={listingReelTransformUrl(videoUrl, reelWidth ?? REEL_WIDTH_BY_TRIGGER[trigger])}
        aria-label={`Walkthrough video${ofAddress}`}
        controls={isActive}
        muted
        playsInline
        preload="none"
        className={cn(
          'absolute inset-0 size-full bg-od-bg-inverse-primary object-contain',
          isActive ? 'pointer-events-auto z-[2] opacity-100' : 'opacity-0',
        )}
        tabIndex={isActive ? undefined : -1}
        aria-hidden={isActive ? undefined : true}
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
      />
      {isActive && (
        <Box
          ref={closeFocusRef}
          aria-label="Close video and return to photos"
          analyticsName="marketplace-pdp-walkthrough-video-close"
          onClick={handleDismiss}
          className="pointer-events-auto absolute right-6 top-6 z-[3] size-9 cursor-pointer items-center justify-center rounded-full border-od-border-tertiary bg-od-bg-primary p-0"
        >
          <IconX size={24} />
        </Box>
      )}
    </Box>
  );
};
